import React, { useEffect, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { SFiltersPanel } from '@avalara/skylab-sdk/react';
import LanguageStringResolver from '../fields/language/LanguageStringResolver';
import LANGUAGES_CONTROLLER, { DEFAULT_LANGUAGE_STRINGS } from '../../controllers/languages';

const CompaniesFilterPanel = ({
    open,
    onDismiss,
    filteringConfig,
    shouldRestoreOpen,
    setFilteringConfig,
    languageStrings
}) => {
    const panelWrapperRef = useRef(null);
    const [name, setName] = useState(filteringConfig.name || '');
    const [code, setCode] = useState(filteringConfig.code || '');
    const [vatNumber, setVatNumber] = useState(filteringConfig.vatNumber || '');

    const setFilteringValues = () => {
        setFilteringConfig({
            name: name.trim(),
            code: code.trim(),
            vatNumber: vatNumber.trim()
        });
    };

    const applyFiltersOnEnter = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            setFilteringValues();
        }
    };

    const resetFilteringValues = () => {
        setName('');
        setCode('');
        setVatNumber('');
        setFilteringConfig({ name: '', code: '', vatNumber: '' });
    };

    const filtersPanelI18n = useMemo(() => ({
        'filtersPanel.done': LANGUAGES_CONTROLLER.formatUtilFormatter(languageStrings, 'CompaniesFilter_Apply')
    }), [languageStrings]);

    useEffect(() => {
        if (!open) {
            return;
        }

        setName(filteringConfig.name || '');
        setCode(filteringConfig.code || '');
        setVatNumber(filteringConfig.vatNumber || '');
    }, [open, shouldRestoreOpen, filteringConfig.name, filteringConfig.code, filteringConfig.vatNumber]);

    useEffect(() => {
        if (!open) {
            return;
        }

        const panel = panelWrapperRef.current?.firstElementChild;
        if (!panel) {
            return;
        }

        // Workaround: Escape from child modal can close the web component internally
        // while React state still expects it open, so force open during restore.
        panel.setAttribute('open', '');
        panel.open = true;
    }, [open, shouldRestoreOpen]);

    return (
        <div ref={panelWrapperRef}>
            <SFiltersPanel
                open={open}
                className="mr30"
                data-testid="companies-filter-panel"
                i18n={filtersPanelI18n}
                onS-dismiss={onDismiss}
                onS-reset={resetFilteringValues}
                onS-apply={setFilteringValues}
            >
                <div slot="filters-panel-content">
                    <div className="full-width mb10">
                        <label htmlFor="companies-filter-name-input">
                            <LanguageStringResolver string="CompaniesFilter_Name" />
                        </label>
                        <input
                            id="companies-filter-name-input"
                            data-testid="companies-filter-name"
                            type="text"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            onKeyDown={applyFiltersOnEnter}
                        />
                    </div>
                    <div className="full-width mb10">
                        <label htmlFor="companies-filter-code-input">
                            <LanguageStringResolver string="CompaniesFilter_Code" />
                        </label>
                        <input
                            id="companies-filter-code-input"
                            data-testid="companies-filter-code"
                            type="text"
                            value={code}
                            onChange={(e) => setCode(e.target.value)}
                            onKeyDown={applyFiltersOnEnter}
                        />
                    </div>
                    <div className="full-width mb10">
                        <label htmlFor="companies-filter-vat-number-input">
                            <LanguageStringResolver string="CompaniesFilter_VATNumber" />
                        </label>
                        <input
                            id="companies-filter-vat-number-input"
                            data-testid="companies-filter-vat-number"
                            type="text"
                            value={vatNumber}
                            onChange={(e) => setVatNumber(e.target.value)}
                            onKeyDown={applyFiltersOnEnter}
                        />
                    </div>
                </div>
            </SFiltersPanel>
        </div>
    );
};


function mapStateToProps(state) {
    return {
        languageStrings: (state.languages && state.languages.data) || DEFAULT_LANGUAGE_STRINGS
    };
}

export default connect(mapStateToProps)(CompaniesFilterPanel);
